import React, { useContext, useState } from 'react';
import { Table, Tag, Switch, Space, Typography, Tooltip, Card, Badge } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PercentageOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  NumberOutlined
} from '@ant-design/icons';
import moment from 'moment';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import { TOGGLE_DISCOUNT_CODE_FOR_VENUE } from 'Mutations/DiscountCodes';
import { TOGGLE_DISCOUNT_CODE } from 'Mutations/Admin/DiscountCodes';
import { SnackbarContext } from 'Store/SnackbarContext';

const { Text } = Typography;

const StyledCard = styled(Card)`
  margin-top: 24px;

  .ant-table-thead > tr > th {
    font-family: IBMPlexSans-SemiBold;
    font-size: 12px;
    font-weight: 600;
    color: rgb(79, 93, 109);
    text-transform: uppercase;
    background-color: rgb(250, 250, 250);
  }

  .ant-table-tbody > tr > td {
    font-size: 13px;
  }

  .code-cell {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 14px;
    color: rgb(51, 63, 72);
  }

  .stat-text {
    font-size: 12px;
    color: rgb(100, 116, 139);
  }

  .label-text {
    font-size: 11px;
    color: rgb(148, 163, 184);
    text-transform: uppercase;
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatsGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DiscountCodesTable = ({ discountCodes = [], loading = false, refetch, venueId, isAdmin = false }) => {
  const { showSnackbar } = useContext(SnackbarContext);
  const [toggleLoading, setToggleLoading] = useState({});

  // Use different mutations based on context (admin vs superadmin)
  const [toggleDiscountCodeForVenue] = useMutation(TOGGLE_DISCOUNT_CODE_FOR_VENUE, {
    onCompleted: data => {
      if (data.toggleDiscountCodeForVenue.success) {
        showSnackbar('Discount code updated successfully', { error: false, duration: 3000 });
        refetch && refetch();
      } else {
        showSnackbar(data.toggleDiscountCodeForVenue.error || 'Failed to update discount code', { error: true, duration: 5000 });
      }
    },
    onError: error => {
      showSnackbar(error?.message?.replace('GraphQL error: ', '') || 'Failed to update discount code', { error: true, duration: 5000 });
    }
  });

  const [toggleDiscountCodeAdmin] = useMutation(TOGGLE_DISCOUNT_CODE, {
    onCompleted: data => {
      if (data.toggleDiscountCode.success) {
        showSnackbar('Discount code updated successfully', { error: false, duration: 3000 });
        refetch && refetch();
      } else {
        showSnackbar(data.toggleDiscountCode.error || 'Failed to update discount code', { error: true, duration: 5000 });
      }
    },
    onError: error => {
      showSnackbar(error?.message?.replace('GraphQL error: ', '') || 'Failed to update discount code', { error: true, duration: 5000 });
    }
  });

  const handleToggle = async (record, checked) => {
    setToggleLoading({ ...toggleLoading, [record.id]: true });

    try {
      if (isAdmin) {
        // Admin mutation - doesn't need venueId
        await toggleDiscountCodeAdmin({
          variables: {
            input: {
              id: record.id,
              isActive: checked
            }
          }
        });
      } else {
        // SuperAdmin mutation - needs venueId
        await toggleDiscountCodeForVenue({
          variables: {
            input: {
              id: record.id,
              venueId: venueId,
              isActive: checked
            }
          }
        });
      }
    } finally {
      setToggleLoading({ ...toggleLoading, [record.id]: false });
    }
  };

  const formatDateTime = dateString => {
    if (!dateString) return null;
    return moment(dateString).format('MMM D, YYYY h:mm A');
  };

  const getStatusTag = record => {
    const now = moment();
    const isActive = record.isActive;
    const validFrom = record.validFrom ? moment(record.validFrom) : null;
    const validUntil = record.validUntil ? moment(record.validUntil) : null;
    const isExpired = validUntil && now.isAfter(validUntil);
    const notStarted = validFrom && now.isBefore(validFrom);
    const maxUsesReached = record.maxUses && record.currentUses >= record.maxUses;

    if (!isActive) {
      return <Tag color="default">Inactive</Tag>;
    }
    if (maxUsesReached) {
      return <Tag color="red">Max Uses Reached</Tag>;
    }
    if (isExpired) {
      return <Tag color="red">Expired</Tag>;
    }
    if (notStarted) {
      return <Tag color="blue">Scheduled</Tag>;
    }
    return <Tag color="green">Active</Tag>;
  };

  const getDiscountDisplay = record => {
    if (record.discountType === 'percentage') {
      return (
        <Space>
          <PercentageOutlined style={{ color: '#1890ff' }} />
          <Text strong>{record.discountPercentage}% off</Text>
        </Space>
      );
    } else {
      return (
        <Space>
          <DollarOutlined style={{ color: '#52c41a' }} />
          <Text strong>${record.discountAmount?.toFixed(2)} off</Text>
        </Space>
      );
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      fixed: 'left',
      width: 150,
      render: (code, record) => (
        <InfoSection>
          <div className="code-cell">{code}</div>
          {getStatusTag(record)}
        </InfoSection>
      )
    },
    {
      title: 'Discount',
      key: 'discount',
      width: 180,
      render: (_, record) => (
        <InfoSection>
          {getDiscountDisplay(record)}
          {record.minOrderAmount && <Text className="stat-text">Min order: ${record.minOrderAmount.toFixed(2)}</Text>}
        </InfoSection>
      )
    },
    {
      title: 'Usage',
      key: 'usage',
      width: 140,
      render: (_, record) => {
        const usagePercent = record.maxUses ? Math.round((record.currentUses / record.maxUses) * 100) : 0;

        return (
          <InfoSection>
            <Space>
              <NumberOutlined />
              <Text strong>{record.currentUses || 0}</Text>
              {record.maxUses && <Text className="stat-text">/ {record.maxUses}</Text>}
              {!record.maxUses && <Text className="stat-text">uses</Text>}
            </Space>
            {record.maxUses && (
              <div style={{ width: '100%', background: '#f0f0f0', height: 6, borderRadius: 3 }}>
                <div
                  style={{
                    width: `${Math.min(usagePercent, 100)}%`,
                    background: usagePercent >= 100 ? '#ff4d4f' : usagePercent >= 80 ? '#faad14' : '#52c41a',
                    height: '100%',
                    borderRadius: 3,
                    transition: 'width 0.3s'
                  }}
                />
              </div>
            )}
          </InfoSection>
        );
      }
    },
    {
      title: 'Valid Period',
      key: 'validity',
      width: 200,
      render: (_, record) => {
        const hasValidFrom = record.validFrom;
        const hasValidUntil = record.validUntil;

        if (!hasValidFrom && !hasValidUntil) {
          return <Text className="stat-text">No expiration</Text>;
        }

        return (
          <InfoSection>
            {hasValidFrom && (
              <Text className="stat-text">
                <CalendarOutlined /> From: {formatDateTime(record.validFrom)}
              </Text>
            )}
            {hasValidUntil && (
              <Text className="stat-text">
                <CalendarOutlined /> Until: {formatDateTime(record.validUntil)}
              </Text>
            )}
          </InfoSection>
        );
      }
    },
    {
      title: 'Venue',
      dataIndex: ['venue', 'name'],
      key: 'venue',
      width: 180,
      render: (venueName, record) => (
        <InfoSection>
          <Text>{venueName || 'Global'}</Text>
          {record.venue && <Text className="stat-text">ID: {record.venue.id}</Text>}
        </InfoSection>
      )
    },
    {
      title: 'Created By',
      key: 'createdBy',
      width: 180,
      render: (_, record) => {
        const creator = record.createdBy;
        if (!creator) return <Text className="stat-text">Unknown</Text>;

        const name = `${creator.firstName || ''} ${creator.lastName || ''}`.trim() || creator.email;

        return (
          <Tooltip title={creator.email}>
            <InfoSection>
              <Space size="small">
                <UserOutlined />
                <Text>{name}</Text>
              </Space>
              <Text className="stat-text">{formatDateTime(record.createdAt)}</Text>
            </InfoSection>
          </Tooltip>
        );
      }
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: {
        showTitle: false
      },
      render: description => (
        <Tooltip placement="topLeft" title={description}>
          <Text className="stat-text">{description || '-'}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Active',
      key: 'isActive',
      fixed: 'right',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.isActive}
          loading={toggleLoading[record.id]}
          onChange={checked => handleToggle(record, checked)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      )
    }
  ];

  // Filter out venue column for admins (they already know their venue)
  const visibleColumns = isAdmin ? columns.filter(col => col.key !== 'venue') : columns;

  if (!discountCodes || discountCodes.length === 0) {
    return (
      <StyledCard>
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgb(100, 116, 139)' }}>
          <Text>No discount codes found for this venue.</Text>
        </div>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Table
        columns={visibleColumns}
        dataSource={discountCodes}
        rowKey="id"
        loading={loading}
        scroll={{ x: isAdmin ? 1220 : 1400 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: total => `Total ${total} discount codes`
        }}
      />
    </StyledCard>
  );
};

export default DiscountCodesTable;
